import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  formatDate,
  IdChip,
  LoadingState,
  PageLayout,
  type PageTab,
  PageTabs,
  type StatusTone,
} from '@repo/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  Building2Icon,
  CreditCardIcon,
  GaugeIcon,
  HistoryIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Trash2Icon,
  UsersIcon,
} from 'lucide-react'
import { useState } from 'react'

import { OrgAvatar } from '@/components/org-cell'
import { AiUsage } from '@/components/org-detail/ai-usage'
import { ChannelsTab } from '@/components/org-detail/channels-tab'
import { ComplianceTab } from '@/components/org-detail/compliance-tab'
import { DeleteOrganizationDialog } from '@/components/org-detail/delete-organization-dialog'
import { OverviewTab } from '@/components/org-detail/overview-tab'
import { useOrgDirectory } from '@/components/org-detail/shared'
import { SubscriptionTab } from '@/components/org-detail/subscription-tab'
import { TeamTab } from '@/components/org-detail/team-tab'
import { StatusBadge } from '@/components/status-badge'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { useAdminOverview } from '@/hooks/use-admin-overview'
import { fetchOrganizationDeletion, fetchOrganizationSettings } from '@/lib/api'
import type { OrganizationSettings, OrgDirectoryChannel } from '@/lib/api-types'
import { attentionSignals } from '@/lib/attention'

const TABS = [
  { value: 'visao-geral', label: 'Visão geral', icon: GaugeIcon },
  { value: 'assinatura', label: 'Assinatura', icon: CreditCardIcon },
  { value: 'equipe', label: 'Equipe', icon: UsersIcon },
  { value: 'canais', label: 'Canais', icon: MessageCircleIcon },
  { value: 'ia', label: 'Uso de IA', icon: SparklesIcon },
  { value: 'conformidade', label: 'Conformidade', icon: ShieldCheckIcon },
] as const

type Tab = (typeof TABS)[number]['value']

/** Nomes antigos das abas, para links salvos continuarem abrindo. */
const LEGACY_TABS: Record<string, Tab> = {
  cobranca: 'assinatura',
  acesso: 'equipe',
  'ia-canais': 'canais',
}

// A aba fica na URL: recarregar ou mandar o link abre na mesma aba.
export const Route = createFileRoute('/_app/organizacoes/$orgId')({
  validateSearch: (search: Record<string, unknown>): { aba?: Tab } => {
    const raw = typeof search.aba === 'string' ? search.aba : ''
    const tab = LEGACY_TABS[raw] ?? raw
    return {
      aba:
        tab !== 'visao-geral' && TABS.some((item) => item.value === tab)
          ? (tab as Tab)
          : undefined,
    }
  },
  component: OrganizationDetailPage,
})

/** Ponto de alerta da aba Assinatura: atraso, suspensão, contrato faltando. */
function subscriptionAlert(settings: OrganizationSettings): StatusTone | null {
  const sub = settings.subscription
  if (!sub) return null
  if (sub.status === 'SUSPENDED') return 'destructive'
  if (
    (sub.plan === 'ENTERPRISE' || sub.plan === 'PARTNER') &&
    !settings.contract
  ) {
    return 'destructive'
  }
  if (sub.status === 'PAST_DUE') return 'warning'
  return null
}

function channelsAlert(channels: OrgDirectoryChannel[]): StatusTone | null {
  if (channels.some((c) => ['ERROR', 'AUTH_FAILED'].includes(c.status))) {
    return 'destructive'
  }
  if (channels.some((c) => c.status === 'QR_EXPIRED')) return 'warning'
  return null
}

const DELETION_STATUS: Record<string, string> = {
  PENDING: 'pendente',
  RUNNING: 'em andamento',
  FAILED: 'falhou',
  CANCELLED: 'cancelada',
}

function OrganizationDetailPage() {
  const { orgId } = Route.useParams()
  const { aba = 'visao-geral' } = Route.useSearch()
  const navigate = useNavigate()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const settings = useQuery({
    queryKey: ['organization-settings', orgId],
    queryFn: () => fetchOrganizationSettings(orgId),
  })
  const deletion = useQuery({
    queryKey: ['organization-deletion', orgId],
    queryFn: () => fetchOrganizationDeletion(orgId),
  })
  const directory = useOrgDirectory(orgId)
  const overview = useAdminOverview()

  const back = (
    <Button
      variant="ghost"
      size="icon"
      title="Voltar para Organizações"
      onClick={() => void navigate({ to: '/organizacoes' })}
    >
      <ArrowLeftIcon className="size-4" />
    </Button>
  )

  if (settings.isLoading) {
    return (
      <PageLayout title="Organização" back={back}>
        <LoadingState />
      </PageLayout>
    )
  }

  if (!settings.data) {
    return (
      <PageLayout title="Organização" back={back}>
        <EmptyState
          variant="inline"
          className="min-h-80"
          icon={Building2Icon}
          title="Organização não encontrada"
          description="Ela pode ter sido excluída, ou o ID está errado."
        />
      </PageLayout>
    )
  }

  const data = settings.data
  const { org, subscription } = data
  const pendingDeletion =
    deletion.data && DELETION_STATUS[deletion.data.status]
      ? deletion.data
      : null

  const overviewRow = overview.data?.organizations.find(
    (row) => row.id === orgId
  )
  const topSignal = overviewRow
    ? attentionSignals(overviewRow, {
        usdToBrl: overview.data?.exchangeRate.rate ?? null,
      })[0]
    : undefined
  const channels = directory.data?.channels ?? []
  const tabs: PageTab[] = TABS.map((tab) => ({
    ...tab,
    count:
      tab.value === 'equipe'
        ? data.usage.members
        : tab.value === 'canais' && directory.data
          ? channels.length
          : null,
    alert:
      tab.value === 'visao-geral'
        ? topSignal &&
          (topSignal.tone === 'destructive' || topSignal.tone === 'warning')
          ? topSignal.tone
          : null
        : tab.value === 'assinatura'
          ? subscriptionAlert(data)
          : tab.value === 'canais'
            ? channelsAlert(channels)
            : null,
  }))

  return (
    <Tabs
      value={aba}
      onValueChange={(value) =>
        void navigate({
          to: '/organizacoes/$orgId',
          params: { orgId },
          search: {
            aba: value === 'visao-geral' ? undefined : (value as Tab),
          },
          replace: true,
        })
      }
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <PageLayout
        back={back}
        avatar={<OrgAvatar name={org.name} logo={org.logo} size="md" />}
        tabs={<PageTabs items={tabs} label="Seções da organização" />}
        title={org.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-x-2">
            {org.slug ?? 'sem slug'} · <IdChip value={org.id} size={12} />
          </span>
        }
        meta={
          <div className="flex flex-wrap gap-1.5">
            <StatusBadge value={subscription?.status} />
            <StatusBadge value={subscription?.plan} tone="neutral" />
          </div>
        }
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="Ações da organização"
              >
                <MoreHorizontalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {deletion.data ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/organizacoes/$orgId/exclusao" params={{ orgId }}>
                      <HistoryIcon />
                      Ver última exclusão
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => setDeleteOpen(true)}
              >
                <Trash2Icon />
                Excluir organização…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        {pendingDeletion ? (
          <Alert variant="destructive">
            <ShieldAlertIcon />
            <AlertTitle>
              Exclusão {DELETION_STATUS[pendingDeletion.status]} desde{' '}
              {formatDate(pendingDeletion.createdAt)}
            </AlertTitle>
            <AlertDescription>
              <Link
                to="/organizacoes/$orgId/exclusao"
                params={{ orgId }}
                className="underline"
              >
                Ver as etapas e o que ficou pendente
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}

        <TabsContent value="visao-geral">
          <OverviewTab orgId={orgId} settings={data} />
        </TabsContent>
        <TabsContent value="assinatura">
          <SubscriptionTab orgId={orgId} settings={data} />
        </TabsContent>
        <TabsContent value="equipe">
          <TeamTab orgId={orgId} settings={data} />
        </TabsContent>
        <TabsContent value="canais">
          <ChannelsTab orgId={orgId} />
        </TabsContent>
        <TabsContent value="ia">
          <AiUsage orgId={orgId} />
        </TabsContent>
        <TabsContent value="conformidade">
          <ComplianceTab orgId={orgId} />
        </TabsContent>

        <DeleteOrganizationDialog
          orgId={orgId}
          orgName={org.name}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      </PageLayout>
    </Tabs>
  )
}
